import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Text from '@clearscore/rainbow.text';
import Criteria from '@clearscore/rainbow.criteria';
import toTitleCase from '@clearscore/normalize.title-case';

import styles from './text-input.css';
import { themes, types } from './constants';

const TextInput = ({
    autoComplete,
    errorMessage,
    isDirty, // keep isDirty prop, even though it's not used, so integration with final-form is smoooth.
    isDisabled,
    isInvalid,
    isValid,
    name,
    id,
    onChange,
    prepend,
    suffix,
    theme,
    type,
    value,
    dataId,
    elementRef,
    isDockedTop,
    ...restProps
}) => {
    const themeClass = styles[`isTheme${toTitleCase(theme)}`];
    const inputClass = cx(styles.inputWrapper, themeClass, {
        [styles.isDisabled]: isDisabled,
        [styles.isInvalid]: isInvalid,
        [styles.isValid]: isValid,
        [styles.shortPrepend]: prepend && prepend.length === 1,
        [styles.mediumPrepend]: prepend && prepend.length === 2,
        [styles.longPrepend]: prepend && prepend.length > 2,
        [styles.isDockedTop]: isDockedTop,
    });
    const prependClass = cx(styles.prependWrapper, themeClass, {
        [styles.isInvalid]: isInvalid,
        [styles.isDisabled]: isDisabled,
        [styles.isValid]: isValid,
    });
    const suffixClass = cx(styles.suffixWrapper, themeClass, {
        [styles.isDisabled]: isDisabled,
    });

    return (
        <div className={styles.component} data-id={dataId || name} data-qa="text-input">
            <div className={styles.wrapper}>
                <If condition={prepend}>
                    <div className={prependClass} data-qa="prepend">
                        <Text.Body1>{prepend}</Text.Body1>
                    </div>
                </If>
                <input
                    autoComplete={autoComplete}
                    className={inputClass}
                    disabled={isDisabled}
                    name={name}
                    onChange={onChange}
                    type={type}
                    value={value}
                    data-qa="input"
                    ref={elementRef}
                    id={id || name}
                    {...restProps}
                />
                <If condition={suffix}>
                    <div className={suffixClass} data-qa="suffix">
                        <Text.Body1>{suffix}</Text.Body1>
                    </div>
                </If>
            </div>
            <If condition={errorMessage}>
                <div className={styles.errorMessage} data-qa="error-message">
                    <Criteria theme={theme} type={Criteria.types.WARNING}>
                        {errorMessage}
                    </Criteria>
                </div>
            </If>
        </div>
    );
};

TextInput.propTypes = {
    autoComplete: PropTypes.string,
    errorMessage: PropTypes.string,
    isDirty: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isInvalid: PropTypes.bool,
    isValid: PropTypes.bool,
    name: PropTypes.string.isRequired,
    id: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    prepend: PropTypes.string,
    suffix: PropTypes.string,
    theme: PropTypes.oneOf(Object.values(themes)),
    type: PropTypes.oneOf(Object.values(types)),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    dataId: PropTypes.string,
    elementRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.object })]),
    isDockedTop: PropTypes.bool,
};

TextInput.defaultProps = {
    autoComplete: 'off',
    errorMessage: '',
    id: '',
    isDirty: false,
    isDisabled: false,
    isInvalid: false,
    isValid: false,
    prepend: null,
    suffix: null,
    theme: themes.LIGHT,
    type: types.TEXT,
    dataId: undefined,
    elementRef: null,
    isDockedTop: false,
};

TextInput.themes = themes;
TextInput.types = types;

export default TextInput;
